import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import StatCard from "../StatCard";
import TabFreshnessNote from "./TabFreshnessNote";
import { Link2, Globe, BarChart3 } from "lucide-react";

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

export default function BacklinksTab({ client }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .post(`/seo/backlinks?client_id=${client.id}`, { domain: client.domain, limit: 50 })
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [client.id, client.domain]);

  const summary = data?.summary || {};
  const refDomains = data?.referring_domains?.items || [];
  const anchors = data?.anchors?.items || [];
  const links = data?.backlinks?.items || [];
  const noBacklinkData = !loading && !error &&
    !summary.backlinks && refDomains.length === 0 && anchors.length === 0 && links.length === 0;

  return (
    <div className="space-y-5" data-testid="backlinks-tab">
      <TabFreshnessNote testid="backlinks-freshness" savedAt={data?.saved_at}>
        Fetched live from DataForSEO Backlinks on each visit · No scheduled cron — refresh by reopening the tab.
      </TabFreshnessNote>

      {loading && (
        <div className="text-sm text-[#a0aec0] font-mono-tight">Fetching backlinks…</div>
      )}
      {error && (
        <div className="text-sm text-[#f56565]">Error: {error}</div>
      )}

      {noBacklinkData && (
        <div className="card-flat p-4 text-sm text-[#4a5568] border-l-4 border-l-[#ed8936]" data-testid="bl-empty-banner">
          <span className="text-[#ed8936] font-semibold">Heads up.</span> DataForSEO returned no backlink data for{" "}
          <span className="font-mono-tight">{client.domain}</span>. The account likely needs the Backlinks API subscription.
        </div>
      )}

      {!loading && !error && (<>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <StatCard label="Backlinks" value={fmt(summary.backlinks)} icon={Link2} testid="bl-total" />
        <StatCard label="Ref. domains" value={fmt(summary.referring_domains)} icon={Globe} testid="bl-ref-domains" />
        <StatCard label="Ref. IPs" value={fmt(summary.referring_ips)} icon={BarChart3} testid="bl-ref-ips" />
        <StatCard label="Domain rank" value={summary.rank ?? "—"} icon={BarChart3} testid="bl-rank" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Top referring domains" testid="ref-domains-panel">
          <div className="grid grid-cols-12 gap-x-4 px-5 py-2.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]">
            <div className="col-span-7">Domain</div>
            <div className="col-span-2 text-right">Rank</div>
            <div className="col-span-3 text-right">Backlinks</div>
          </div>
          <div className="divide-y divide-[#edf2f7] max-h-80 overflow-y-auto">
            {refDomains.slice(0, 25).map((d, i) => (
              <div key={i} className="grid grid-cols-12 gap-x-4 px-5 py-2 text-sm hover:bg-[#f7fafc]">
                <div className="col-span-7 truncate font-mono-tight text-[#4a5568]">{d.domain}</div>
                <div className="col-span-2 text-right font-mono-tight">{d.rank ?? "—"}</div>
                <div className="col-span-3 text-right font-mono-tight">{fmt(d.backlinks)}</div>
              </div>
            ))}
            {refDomains.length === 0 && <div className="p-6 text-center text-sm text-[#a0aec0]">No data</div>}
          </div>
        </Panel>

        <Panel title="Top anchors" testid="anchors-panel">
          <div className="grid grid-cols-12 gap-x-4 px-5 py-2.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]">
            <div className="col-span-8">Anchor</div>
            <div className="col-span-2 text-right">Backlinks</div>
            <div className="col-span-2 text-right">Domains</div>
          </div>
          <div className="divide-y divide-[#edf2f7] max-h-80 overflow-y-auto">
            {anchors.slice(0, 25).map((a, i) => (
              <div key={i} className="grid grid-cols-12 gap-x-4 px-5 py-2 text-sm hover:bg-[#f7fafc]">
                <div className="col-span-8 truncate text-[#4a5568]">{a.anchor || <em className="text-[#a0aec0]">(empty)</em>}</div>
                <div className="col-span-2 text-right font-mono-tight">{fmt(a.backlinks)}</div>
                <div className="col-span-2 text-right font-mono-tight">{fmt(a.referring_domains)}</div>
              </div>
            ))}
            {anchors.length === 0 && <div className="p-6 text-center text-sm text-[#a0aec0]">No data</div>}
          </div>
        </Panel>
      </div>

      <Panel title="Top backlinks" testid="backlinks-panel">
        <div className="grid grid-cols-12 gap-x-4 px-5 py-2.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]">
          <div className="col-span-6">Source URL</div>
          <div className="col-span-3">Anchor</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-1 text-right">Rank</div>
        </div>
        <div className="divide-y divide-[#edf2f7] max-h-96 overflow-y-auto">
          {links.slice(0, 50).map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-x-4 px-5 py-2 text-sm hover:bg-[#f7fafc]">
              <div className="col-span-6 truncate text-xs font-mono-tight">
                <a href={l.url_from} target="_blank" rel="noreferrer" className="text-[#4a5568] hover:text-[#2c7a7b]">
                  {(l.url_from || "").replace(/^https?:\/\//, "").slice(0, 60)}
                </a>
              </div>
              <div className="col-span-3 truncate text-xs text-[#4a5568]">{l.anchor || <em className="text-[#a0aec0]">—</em>}</div>
              <div className="col-span-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${l.dofollow ? "bg-[#48bb78]/15 text-[#2f855a]" : "bg-[#edf2f7] text-[#4a5568]"}`}>
                  {l.dofollow ? "dofollow" : "nofollow"}
                </span>
              </div>
              <div className="col-span-1 text-right font-mono-tight">{l.rank ?? "—"}</div>
            </div>
          ))}
          {links.length === 0 && <div className="p-6 text-center text-sm text-[#a0aec0]">No data</div>}
        </div>
      </Panel>
      </>)}
    </div>
  );
}

function Panel({ title, testid, children }) {
  return (
    <div className="card-soft overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-[#edf2f7]">
        <h3 className="font-display text-base text-[#1a202c]">{title}</h3>
      </div>
      {children}
    </div>
  );
}
